// Partager, choisir un destinataire, signaler — les trois fenêtres qui
// s'ouvrent depuis une publication, un profil, une page ou un reel.
//
// Sorties de `views/posts.js`, dont la carte de publication est passée à React.
// Elles restent en HTML à dessein : une fenêtre s'ouvre, se remplit, se ferme.
// Elle n'a pas d'état qui vive dans le temps, et la traduire n'apporterait rien
// qu'un risque.
//
// Elles sont ici et non dans un composant parce que quatre écrans les appellent,
// dont trois qui ne sont pas encore en React.

import Store from "../store.js";
import { esc, avatar, texteRiche, ilYA, toast, modale, fermerModale } from "../ui.js";
import { APP } from "../config.js";

/** Photo ou vidéo attachée à une publication. */
export const mediaHtml = (p) =>
  p.video
    ? `<video class="post__video" src="${esc(p.video)}" controls playsinline preload="metadata"></video>`
    : p.image
    ? `<img class="post__image" src="${esc(p.image)}" alt="" loading="lazy">`
    : "";

/** Publication d'origine, encadrée, quand il s'agit d'un partage. */
export const citation = (o) => `
  <blockquote class="repartage">
    <a class="repartage__auteur" href="#/profil/${esc(o.authorId)}">
      ${avatar({ nom: o.auteurNom, photo: o.auteurPhoto }, 30)}
      <strong>${esc(o.auteurNom || "Membre")}</strong>
      <span class="petit texte-doux">${esc(ilYA(o.createdAt))}</span>
    </a>
    ${o.texte ? `<div class="repartage__texte">${texteRiche(o.texte)}</div>` : ""}
    ${mediaHtml(o)}
  </blockquote>`;

/** Partage d'une publication : dans son propre fil, en message privé, ou par lien. */
export function partager(p, ctx) {
  // on ne ré-empile pas les partages : on repartage toujours la publication d'origine
  const origine = p.partageDe || {
    id: p.id,
    authorId: p.authorId,
    auteurNom: p.auteurNom,
    auteurPhoto: p.auteurPhoto,
    texte: p.texte,
    image: p.image || "",
    video: p.video || "",
    type: p.type,
    createdAt: p.createdAt,
  };
  /* ⚠️ L'ADRESSE PUBLIQUE, JAMAIS `location.origin`.
     Un lien partagé QUITTE l'appareil : il doit être valable pour celui qui le
     REÇOIT, pas pour celui qui l'envoie. `location.origin` donne l'adresse d'où
     l'on regarde — et dans l'application Android qui emballe le site, c'est
     `https://localhost`. Des liens morts sont partis ainsi en message privé ;
     on en a retrouvé un dans une conversation le 16 août 2026 :
     « https://localhost/#/publication/… ».
     `APP.urlPublique` est fixe et connue de tous ; `store.js` s'en sert déjà
     pour les liens des courriels. Le repli ne sert qu'au développement, si la
     configuration n'était pas chargée. */
  const racine = (APP?.urlPublique || `${location.origin}${location.pathname}`).replace(/\/+$/, "");
  const lien = `${racine}/#/publication/${origine.id}`;

  // Un partage compte, quelle que soit sa forme : repartager, envoyer en privé,
  // copier le lien ou passer par une autre application, c'est le même geste —
  // porter ce texte plus loin. Le compteur va toujours sur la publication
  // d'ORIGINE, jamais sur le repartage, sinon il se perdrait en chemin.
  const compter = () => Store.posts.compterPartage?.(origine.id).catch(() => {});

  const { el } = modale({
    titre: "Partager",
    corps: `
      <label class="label">Ajouter un mot (facultatif)
        <textarea class="champ" id="mot-partage" rows="2" maxlength="500" placeholder="Ce message m'a édifié…"></textarea>
      </label>
      ${citation(origine)}
      <div class="menu-liste" style="margin-top:14px">
        <button class="menu-item" data-p="fil">🔁 Partager dans mon fil</button>
        <button class="menu-item" data-p="prive">✉️ Envoyer en message privé</button>
        <button class="menu-item" data-p="lien">🔗 Copier le lien</button>
        ${navigator.share ? `<button class="menu-item" data-p="natif">📤 Partager vers une autre application</button>` : ""}
      </div>`,
  });

  el.addEventListener("click", async (e) => {
    const b = e.target.closest("[data-p]");
    if (!b) return;
    const mot = el.querySelector("#mot-partage").value.trim();
    const action = b.dataset.p;
    if (action === "lien" || action === "natif") {
      fermerModale();
      compter();
      if (action === "natif") {
        try {
          await navigator.share({ title: `${APP.nom} — ${origine.auteurNom}`, text: origine.texte, url: lien });
        } catch {}
      } else {
        await navigator.clipboard?.writeText(lien);
        toast("Lien copié");
      }
      return;
    }
    if (action === "prive") {
      fermerModale();
      choisirDestinataire(ctx, async (membre) => {
        compter();
        const id = await Store.convos.ensure(membre.uid);
        // Pas de notification : un message privé se signale tout seul, par la
        // pastille de la messagerie. (Voir majBadgeMessages dans app.js.)
        await Store.messages.send(id, `${mot ? mot + "\n" : ""}${lien}`);
        location.hash = `#/messages/${id}`;
      });
      return;
    }
    fermerModale();
    try {
      await Store.posts.add({
        type: "partage",
        authorId: ctx.moi.uid,
        auteurNom: ctx.moi.nom,
        auteurPhoto: ctx.moi.photo || "",
        texte: mot,
        image: "",
        video: "",
        partageDe: origine,
      });
      compter();
      Store.notifs.add(origine.authorId, {
        type: "partage",
        deId: ctx.moi.uid,
        deNom: ctx.moi.nom,
        dePhoto: ctx.moi.photo || "",
        postId: origine.id,
        apercu: (origine.texte || "").slice(0, 80),
      });
      toast("Publication partagée 🔁");
    } catch (err) {
      toast(err.message || "Partage impossible", "erreur");
    }
  });
}

/** Petite liste de membres pour choisir à qui envoyer quelque chose. */
export function choisirDestinataire(ctx, onChoix) {
  const { el } = modale({
    titre: "Envoyer à…",
    corps: `<input class="champ" id="dest-q" type="search" placeholder="Rechercher un membre…" autocomplete="off"><div class="menu-liste" id="dest-liste" style="margin-top:10px">Chargement…</div>`,
  });
  const zone = el.querySelector("#dest-liste");
  const champ = el.querySelector("#dest-q");

  const peupler = async () => {
    const membres = (await Store.users.search(champ.value)).filter((u) => u.uid !== ctx.moi.uid).slice(0, 20);
    zone.innerHTML = membres.length
      ? membres.map((u) => `<button class="menu-item" data-uid="${esc(u.uid)}">${avatar(u, 34)} ${esc(u.nom)}</button>`).join("")
      : `<p class="texte-doux petit">Aucun membre trouvé.</p>`;
  };
  champ.addEventListener("input", () => peupler());
  zone.addEventListener("click", async (e) => {
    const b = e.target.closest("[data-uid]");
    if (!b) return;
    const membre = await Store.users.get(b.dataset.uid);
    fermerModale();
    onChoix(membre);
  });
  peupler();
  champ.focus();
}

/** Boîte de signalement, partagée par les publications et les profils. */
export function signaler({ cibleType, cibleId, auteurId, apercu = "" }) {
  const motifs = APP.motifsSignalement;
  const { el, fermer } = modale({
    titre: "Signaler",
    corps: `
      <p class="texte-doux petit">Un modérateur examinera ce contenu. Merci de veiller sur la communauté.</p>
      ${apercu ? `<blockquote class="apercu">${esc(apercu)}${apercu.length >= 200 ? "…" : ""}</blockquote>` : ""}
      <div class="menu-liste">${motifs.map((m, i) => `<label class="menu-item"><input type="radio" name="motif" value="${esc(m)}" ${i === 0 ? "checked" : ""}> ${esc(m)}</label>`).join("")}</div>
      <textarea class="champ" rows="2" placeholder="Précision (facultatif)"></textarea>`,
    actions: [
      { label: "Annuler" },
      {
        label: "Envoyer le signalement",
        classe: "btn--primaire",
        onClick: async (fond, f) => {
          const motif = fond.querySelector("input[name=motif]:checked")?.value || "Autre";
          const detail = fond.querySelector("textarea").value.trim();
          f();
          try {
            await Store.reports.add({ cibleType, cibleId, auteurId, motif, detail });
            toast("Signalement envoyé. Merci 🙏");
          } catch {
            toast("Envoi impossible", "erreur");
          }
        },
      },
    ],
  });
  return { el, fermer };
}
